import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { FilterType } from './models';
import {
  INTERNAL_SINGLE_FILTER_BAR_GUARD_TOKEN,
  InternalSingleFilterBarGuardI,
  SingleFilterGuardAction,
} from './internal-single-filter-bar.guard';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Component({
  selector: 'miq-internal-single-filter-bar',
  styleUrls: ['single-filter-bar.component.scss'],
  templateUrl: 'single-filter-bar.component.html',
  standalone: false,
})
export class InternalSingleFilterBarComponent implements OnChanges {
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;
  @Input() filters: FilterType[];
  @Input() selected: string | number;
  @Input() useProgrammaticChanges = false;
  @Output() itemSelected = new EventEmitter();

  private isProgrammaticChange = false;

  constructor(
    @Inject(INTERNAL_SINGLE_FILTER_BAR_GUARD_TOKEN) private guard: InternalSingleFilterBarGuardI,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  // changes used for testing
  ngOnChanges(_changes: SimpleChanges): void {
    // For future reference:
    // the setSelectedIndex function should only be triggered
    // when there is change in the filters or in the selected item
    this.#setSelectedIndex(this.selected);
  }

  getIndex(selected: string | number) {
    const index = this.filters?.findIndex(f => f.key === selected);
    return index >= 0 ? index : 0;
  }

  private onTabChange = (index: number, action: SingleFilterGuardAction) => {
    // Don't emit if this is a programmatic change (from input binding)
    if (this.useProgrammaticChanges && this.isProgrammaticChange) {
      this.isProgrammaticChange = false;
      return;
    }

    // Emit change only when the selected item has changed
    if (this.tabGroup.selectedIndex !== index || action === SingleFilterGuardAction.skipTabChange) {
      return;
    }

    // If the guard does not allow to change the selected tab keep the current selection
    if (action === SingleFilterGuardAction.resetSelection) {
      this.tabGroup.selectedIndex = this.getIndex(this.selected);
      return;
    }
    const indexForFilter = this.filters[index] != null ? index : this.tabGroup.selectedIndex;
    if (this.filters[indexForFilter]?.key) {
      return this.itemSelected.emit(this.filters[indexForFilter].key);
    }
    // This shouldn't be happening but added as a double check
    this.logger.warn('The selected tab does not exists as part of the filters');
  };

  tabChange(event: MatTabChangeEvent): void {
    const { index } = event;
    const currentSelectedIndex = this.getIndex(this.selected);
    this.guard
      .beforeSelectionChange({ currentSelectedIndex, newSelectedIndex: index })
      .subscribe(action => this.onTabChange(index, action));
  }

  #setSelectedIndex(selected: string | number): void {
    // Use the timeout to wait for the filters to be ready
    // so the selected tab can be updated
    setTimeout(() => {
      if (this.tabGroup) {
        const newIndex = this.getIndex(selected);
        // Only set the programmatic flag if we're actually changing the index
        // If the index is already correct, no change event will fire, so the flag would never be reset
        if (this.tabGroup.selectedIndex !== newIndex) {
          this.isProgrammaticChange = true;
        }
        this.tabGroup.selectedIndex = newIndex;
      }
    });
  }
}
